import React from 'react'
import cn from 'classnames'
import withSSR from 'js/components/withSSR/withSSR'
import Spinner from 'js/components/Spinner/Spinner'
import ErrorPage from 'js/pages/ErrorPage'
import Mask from '../../../components/Mask'
import { runQuery } from 'js/dato'
import {
  categoryDataQuery,
  allStoriesQuery,
  recipesDataQuery,
} from 'js/pages/NutritionExplained/queries/categoryQuery'
import NutritionFooter from '../components/NutritionFooter'
import NutritionHeader from '../components/NutritionHeader'
import DatoImage from '../../../components/DatoRichText/DatoImage'
import CategoryListing from './CategoryListing'
import FeaturedStories from './FeaturedStories'
import AppPromo from '../components/AppPromo'
import EmptyState from './EmptyState'
import { Tabs, Tab } from './Tabs'
import DatoMeta from '../../../components/DatoMeta'
import useBreakpoints from 'js/hooks/useBreakpoints'
import {
  CategoryData_category,
  CategoryData_category_bgImage,
  CategoryData_category_image,
} from '../queries/types/CategoryData'
import { AllStoriesCategory } from '../queries/types/AllStoriesCategory'

type SVGImageProps = {
  image: CategoryData_category_image
}

const SVGImage = ({ image }: SVGImageProps) => (
  <span className="block text-center mb-8 lg:mb-16">
    <DatoImage
      width={image.width}
      height={image.height}
      src={image.url}
      style={{ width: '20vw', maxWidth: 125 }}
      lazy
    />
  </span>
)

type BackgroundImageProps = {
  image: CategoryData_category_bgImage
}

function BackgroundImage({ image }: BackgroundImageProps) {
  const { LG } = useBreakpoints()
  const url = new URL(image.url)
  url.searchParams.append('dpr', LG ? '2' : '1')

  return (
    <div
      className="absolute inset-0"
      style={{
        backgroundImage: `url(${url.toString()})`,
        backgroundSize: 'cover',
        backgroundPosition: `${image.focalPoint?.x * 100}% ${
          image.focalPoint?.y * 100
        }%`,
      }}
    >
      <div
        className="absolute inset-0"
        style={{
          backgroundImage: 'linear-gradient(rgba(0, 0, 0, 0.4), transparent)',
        }}
      />
    </div>
  )
}

type Props = {
  data:
    | ({
        category: {
          type: 'recipes'
          recipeCount: number
        } & CategoryData_category
        allRecipes: {}
      } & Pick<AllStoriesCategory, 'allRecipes'>)
    | ({
        category: {
          type: 'category'
          storyCount: number
          recipeCount: number
        } & CategoryData_category
      } & Pick<
        AllStoriesCategory,
        'featuredStories' | 'allRecipes' | 'allStories'
      >)
  isLoading: boolean
  error: any
}

function NutritionCategory({ data, isLoading, error }: Props) {
  if (isLoading)
    return (
      <div className="absolute inset-0 flex items-center justify-center">
        <Spinner width="50" color="green" />
      </div>
    )

  if (error) {
    return <ErrorPage />
  }

  const { category } = data

  const hasBackgroundImage =
    (category.type === 'category' || category.type === 'recipes') &&
    category.image &&
    category.image.format !== 'svg'

  return (
    <>
      <DatoMeta
        meta={category.meta}
        fallbackDescription={category.description as string}
        canonicalUrl={`https://lifesum.com/nutrition-explained/category/${category.slug}`}
      />
      <div className="bg-Bg pb-80 lg:pb-128">
        <Mask>
          <div className="relative">
            <NutritionHeader transparent={Boolean(hasBackgroundImage)} />
            <div className="bg-BgSub pt-24 lg:pt-40 pb-48 lg:pb-96">
              {hasBackgroundImage && category.bgImage && (
                <BackgroundImage image={category.bgImage} />
              )}
              <div className="o-width-limiter o-width-limiter--narrow relative">
                <h1
                  className={cn(
                    'text-center',
                    hasBackgroundImage && 'xxl:py-64',
                    hasBackgroundImage
                      ? 'lg:py-40 type-huge-fat text-white'
                      : 'lg:pt-40 type-large-fat text-green'
                  )}
                >
                  {category.image?.format === 'svg' && (
                    <SVGImage image={category.image} />
                  )}
                  {category.name}
                </h1>
              </div>
            </div>
          </div>
        </Mask>
        <div className="pb-40 lg:pb-64">
          {category.description && (
            <div className="o-width-limiter">
              <p className="mt-40 text-center leading-body max-w-768 mx-auto type-med-minus">
                {category.description}
              </p>
            </div>
          )}
        </div>
        <FeaturedStories stories={data['featuredStories'] || []} />
        <Tabs>
          <Tab type="stories" count={category['storyCount']}>
            <CategoryListing
              key="stories"
              type="stories"
              initialEntries={data['allStories'] || []}
              category={category}
              showHeading={!data.allRecipes || !data.allRecipes?.length}
            />
          </Tab>
          <Tab type="recipes" count={category.recipeCount}>
            <CategoryListing
              key="recipes"
              type="recipes"
              initialEntries={data.allRecipes || []}
              category={category}
              showHeading={
                category.type === 'recipes'
                  ? false
                  : !data['allStories'] || !data['allStories'].length
              }
            />
          </Tab>
        </Tabs>
        {(!data.allRecipes || data.allRecipes.length === 0) &&
          (!data['allStories'] || data['allStories'].length === 0) && (
            <EmptyState />
          )}
        <div className="mt-56 md:mt-72 lg:mt-128">
          <AppPromo />
        </div>
      </div>
      <NutritionFooter />
    </>
  )
}

NutritionCategory.getInitialProps = ({ params }) => {
  if (params.category)
    return runQuery(categoryDataQuery, { slug: params.category }).then(
      ({ category }) => {
        if (!category) {
          throw new Error(`category not found, category: ${params.category}`)
        }

        return runQuery(allStoriesQuery, { id: category.id }).then(
          ({ meta, recipesMeta, allStories, allRecipes, featuredStories }) => {
            return {
              category: {
                type: 'category',
                storyCount: meta.count,
                recipeCount: recipesMeta.count,
                ...category,
              },
              featuredStories: featuredStories,
              allStories: allStories,
              allRecipes: allRecipes,
            }
          }
        )
      }
    )

  return Promise.all([
    runQuery(categoryDataQuery, { slug: 'recipes' }),
    runQuery(recipesDataQuery),
  ]).then(([{ category }, { meta, allRecipes }]) => {
    return {
      category: {
        type: 'recipes',
        recipeCount: meta.count,
        ...category,
      },
      allRecipes: allRecipes,
    }
  })
}

export default withSSR(NutritionCategory)
